import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Flag } from 'lucide-react';
import { z } from 'zod';

interface Props {
  targetType: 'property' | 'plot' | 'project' | 'user' | 'agency' | 'agent' | 'review';
  targetId: string;
  variant?: 'ghost' | 'outline' | 'link';
  size?: 'sm' | 'default' | 'icon';
  className?: string;
  label?: string;
}

const reasonOptions = [
  { value: 'fraud', label: 'Suspected fraud / scam' },
  { value: 'duplicate', label: 'Duplicate listing' },
  { value: 'wrong_price', label: 'Wrong price or details' },
  { value: 'sold_or_rented', label: 'Already sold / rented' },
  { value: 'spam', label: 'Spam / irrelevant' },
  { value: 'offensive', label: 'Offensive content' },
  { value: 'other', label: 'Other' },
];

const schema = z.object({
  reason: z.string().min(1),
  details: z.string().trim().max(1000).optional().or(z.literal('')),
  email: z.string().trim().email().max(255).optional().or(z.literal('')),
});

export function ReportButton({ targetType, targetId, variant = 'ghost', size = 'sm', className, label = 'Report' }: Props) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const parsed = schema.safeParse({ reason, details, email });
    if (!parsed.success) {
      toast.error('Please pick a reason (and use a valid email if provided).');
      return;
    }
    if (!user && !email.trim()) {
      toast.error('Please provide an email so we can follow up.');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from('reports' as any).insert({
        target_type: targetType,
        target_id: targetId,
        reason,
        details: details.trim() || null,
        reporter_id: user?.id ?? null,
        reporter_email: user ? null : email.trim(),
      });
      if (error) throw error;
      toast.success('Report submitted. Our team will review it shortly.');
      setOpen(false);
      setReason('');
      setDetails('');
      setEmail('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <Flag className="h-4 w-4 mr-1" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report this listing</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>
                {reasonOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm">Details (optional)</Label>
            <Textarea
              value={details}
              onChange={e => setDetails(e.target.value)}
              placeholder="Tell us what's wrong…"
              rows={3}
              maxLength={1000}
            />
          </div>

          {!user && (
            <div>
              <Label className="text-sm">Your email</Label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                maxLength={255}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
